import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/shared/Button';
import { useAuthContext } from '../components/AuthProvider';
import { QueryService } from '../services/QueryService';
import { Toast } from '../components/shared/Toast';
import TextareaWithCounter from '../components/shared/TextareaWithCounter';

const styles = {
	labelMuted: 'block text-dark-blue/60 text-sm font-medium mb-1.5',
	label: 'block text-dark-blue text-sm font-medium mb-1.5',
	input:
		'block w-full border border-lightGrey rounded-lg px-3 py-2.5 text-dark-blue/70 bg-[#F8F8FA] placeholder-gray focus:outline-none',
	messageArea:
		'block w-full border border-lightGrey rounded-lg px-3 py-3 text-dark-blue placeholder-gray focus:outline-none focus:border-purple resize-none'
};

export default function HelpCentrePage() {
	const { user } = useAuthContext();
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleCancel = () => {
		setQuery('');
		navigate('/dashboard');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const trimmedQuery = query.trim();

		if (!trimmedQuery || trimmedQuery.length < 10) {
			Toast.error('Your ticket must be at least 10 characters long.');
			return;
		}

		try {
			setIsSubmitting(true);
			await QueryService.createQuery({ query });
			setQuery('');
			Toast.success(
				'Your support ticket has been submitted successfully. Our team will get back to you shortly.'
			);
		} catch {
			Toast.error('Failed to submit your ticket. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="w-full max-w-2xl">
			<h1 className="text-dark-blue font-bold text-main mt-6 mb-2">Support</h1>
			<p className="text-customGray text-sm mb-6">
				Tell us what’s going wrong and we’ll get back to you. You can also email{' '}
				<a
					href="mailto:info@writeformapp.com"
					className="text-dark-blue font-semibold underline underline-offset-2 hover:opacity-80"
				>
					info@writeformapp.com
				</a>
				.
			</p>

			<div className="bg-white border border-lightGrey rounded-2xl p-6 sm:p-8">
				<form onSubmit={handleSubmit}>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
						<div>
							<label htmlFor="first-name" className={styles.labelMuted}>
								First Name
							</label>
							<input
								type="text"
								id="first-name"
								name="first-name"
								className={styles.input}
								value={user?.firstName || ''}
								readOnly
							/>
						</div>
						<div>
							<label htmlFor="last-name" className={styles.labelMuted}>
								Last Name
							</label>
							<input
								type="text"
								id="last-name"
								name="last-name"
								className={styles.input}
								value={user?.lastName || ''}
								readOnly
							/>
						</div>
					</div>

					<div className="mb-4">
						<label htmlFor="email-address" className={styles.labelMuted}>
							Email Address
						</label>
						<input
							type="email"
							id="email-address"
							name="email-address"
							className={styles.input}
							value={user?.email || ''}
							readOnly
						/>
					</div>

					<div className="mb-6">
						<label htmlFor="query" className={styles.label}>
							Message
						</label>
						<TextareaWithCounter
							id="query"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Write your support ticket message..."
							minLength={10}
							maxLength={500}
							rows={5}
							className={styles.messageArea}
						/>
					</div>

					<div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
						<button
							type="button"
							className="px-5 py-2.5 rounded-xl border border-dark-blue/25 text-base font-medium text-dark-blue hover:bg-[#F2F3F7] disabled:opacity-50"
							onClick={handleCancel}
							disabled={isSubmitting}
						>
							Cancel
						</button>
						<Button
							type="submit"
							className="!py-2.5 !px-6 !rounded-xl !font-medium !text-base !w-auto"
							disabled={isSubmitting || query.trim().length < 10}
						>
							{isSubmitting ? 'Submitting…' : 'Submit'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
